import { getPostOrderAccepted, resetPostOrder } from '@kfc-global/react-shared/redux/Actions/PostOrderAction';
import {
  clearPastOrderAction,
  getPastOrderAction,
  resetOrderAction,
} from '@kfc-global/react-shared/redux/Actions/orderAction';
import { createBasketsAction } from '@kfc-global/react-shared/redux/Actions/CartAction';

import {
  safeUpperCase,
  redirectToPaymentFailurePage,
  redirectToHomePage,
  dispatchCreateBasketsAction,
  checkByParams,
} from '../../../common/utilities/utils';
import {
  ORDER_STATUS,
  DISPOSITION_TYPES,
  PAYMENT_STATUS,
  ORDER_GROUP_STATUS,
  LOCAL_STORAGE_KEYS,
} from '../../../common/constants/SharedConstants';
import { MAX_WAIT_DELIVERY_INTERVAL, MAX_RETRY_COUNT, MAX_RETRY_INTERVAL } from './constants';
import { ChannelManager } from 'common/manager';
import { resetPaymentStatus } from '@kfc-global/react-shared/redux/Actions/PaymentsAction';
import { ROUTE_URL } from 'common/constants/SharedConstants';

export const isStagedAcceptedOrder = orderDetails => {
  return checkOrderStatusWithService(orderDetails) === ORDER_GROUP_STATUS.STAGED_ACCEPTED;
};

export const isOrderPOSCompleted = orderDetails => {
  return checkOrderStatusWithService(orderDetails) === ORDER_GROUP_STATUS.PICKUP_DELIVERY_DINE_IN;
};

export const isDeliveryDistaOrderPOSCompleted = orderDetails => {
  return safeUpperCase(orderDetails?.service) === DISPOSITION_TYPES.DELIVERY && isOrderPOSCompleted(orderDetails);
};

export const isDeliveryDistOrder = orderDetails => {
  return (
    safeUpperCase(orderDetails?.service) === DISPOSITION_TYPES.DELIVERY &&
    (isDeliveryDistaOrderPOSCompleted(orderDetails) ||
      isDeliveryDistaOrderCreateAssign(orderDetails) ||
      isDeliveryDistaOrderIFrame(orderDetails) ||
      isDeliveryDistaOrderDelivered(orderDetails))
  );
};

export const isDeliveryDistaOrderPartner = orderDetails => {
  return isDeliveryDistOrder(orderDetails) && orderDetails?.deliveryPartner;
};

export const isDeliveryDistaOrderPolling = orderDetails => {
  return (
    isDeliveryDistOrder(orderDetails) &&
    !isDeliveryDistaOrderDelivered(orderDetails) &&
    !isDeliveryDistaOrderCancelled(orderDetails)
  );
};

export const isDeliveryDistaOrderPOSCompleteCreateAssign = orderDetails => {
  return isDeliveryDistaOrderCreateAssign(orderDetails) || isDeliveryDistaOrderPOSCompleted(orderDetails);
};

export const isDeliveryDistaOrderCreateAssign = orderDetails => {
  return checkOrderStatusWithService(orderDetails) === ORDER_GROUP_STATUS.DELIVERY_DISTA_CREATE_ASSIGN;
};

export const isDeliveryDistaOrderIFrame = orderDetails => {
  return checkOrderStatusWithService(orderDetails) === ORDER_GROUP_STATUS.DELIVERY_DISTA;
};

export const isDeliveryDistaOrderDelivered = orderDetails => {
  return checkOrderStatusWithService(orderDetails) === ORDER_GROUP_STATUS.DELIVERY_DISTA_DELIVERED;
};

export const isDeliveryDistaOrderCancelled = orderDetails => {
  return checkOrderStatusWithService(orderDetails) === ORDER_GROUP_STATUS.CANCELLED;
};

export const isDeliveryDistaOrderIframeComponents = orderDetails => {
  return (
    isDeliveryDistaOrderIFrame(orderDetails) ||
    isDeliveryDistaOrderCancelled(orderDetails) ||
    isDeliveryDistaOrderDelivered(orderDetails)
  );
};

export const checkOrderStatusWithService = orderDetails => {
  switch (safeUpperCase(orderDetails?.status)) {
    case ORDER_STATUS.STAGED:
    case ORDER_STATUS.ACCEPTED:
      return ChannelManager.shouldGoogleSpotIgnoreOloFailure
        ? ORDER_GROUP_STATUS.PICKUP_DELIVERY_DINE_IN
        : ORDER_GROUP_STATUS.STAGED_ACCEPTED;

    case ORDER_STATUS.SENT_TO_POS_COMPLETED:
      if (
        safeUpperCase(orderDetails?.service) === DISPOSITION_TYPES.PICKUP ||
        safeUpperCase(orderDetails?.service) === DISPOSITION_TYPES.DINE_IN ||
        safeUpperCase(orderDetails?.service) === DISPOSITION_TYPES.DELIVERY
      ) {
        return ORDER_GROUP_STATUS.PICKUP_DELIVERY_DINE_IN;
      }
      break;

    case ORDER_STATUS.CREATED:
    case ORDER_STATUS.ASSIGNED:
      return ORDER_GROUP_STATUS.DELIVERY_DISTA_CREATE_ASSIGN;

    case ORDER_STATUS.PICKED_UP:
    case ORDER_STATUS.OUT_FOR_DELIVERY:
    case ORDER_STATUS.ARRIVED:
      return ORDER_GROUP_STATUS.DELIVERY_DISTA;

    case ORDER_STATUS.DELIVERED:
      return ORDER_GROUP_STATUS.DELIVERY_DISTA_DELIVERED;

    case ORDER_STATUS.CANCELLED:
    case ORDER_STATUS.CANCEL:
      return safeUpperCase(orderDetails?.service) === DISPOSITION_TYPES.DELIVERY
        ? ORDER_GROUP_STATUS.CANCELLED
        : ORDER_GROUP_STATUS.FAILED;

    case ORDER_STATUS.SENT_TO_POS_FAILED:
    case ORDER_STATUS.PAYMENT_FAILED:
      return ORDER_GROUP_STATUS.FAILED;
    // no default
  }
};

export const orderAcceptedUpdate = (orderDetails, setStagedPage, props) => {
  if (!orderDetails?.orderAcceptedLoading) {
    switch (checkOrderStatusWithService(orderDetails)) {
      case ORDER_GROUP_STATUS.STAGED_ACCEPTED:
        setStagedPage(true);
        return Promise.resolve(false);

      case ORDER_GROUP_STATUS.PICKUP_DELIVERY_DINE_IN:
        setStagedPage(false);
        return Promise.resolve(true);

      case ORDER_GROUP_STATUS.DELIVERY_DISTA_CREATE_ASSIGN:
      case ORDER_GROUP_STATUS.DELIVERY_DISTA:
      case ORDER_GROUP_STATUS.DELIVERY_DISTA_DELIVERED:
      case ORDER_GROUP_STATUS.CANCELLED:
        setStagedPage(false);
        return Promise.resolve(false);

      case ORDER_GROUP_STATUS.FAILED:
        localStorage.setItem(PAYMENT_STATUS.STATUS_TEXT, PAYMENT_STATUS.FAILED);
        redirectToPaymentFailurePage(props);
        return Promise.resolve(false);
      // no default
    }
  } else {
    return Promise.resolve(false);
  }
};

export const checkInvalidateCart = (
  dispatch,
  orderDetails,
  orderAccepted,
  cartData,
  userOrderState,
  appStateReducer,
  isBasketLoading,
  status,
  qrCodeScanTime,
) => {
  if (status) {
    const userStatus = appStateReducer.userStatus;
    const sentToPosCompleted =
      ChannelManager.shouldGoogleSpotIgnoreOloFailure ||
      safeUpperCase(orderAccepted?.status) === ORDER_STATUS.SENT_TO_POS_COMPLETED;
    checkByParams(sentToPosCompleted, !isBasketLoading, cartData?.id, userStatus.localized, orderDetails?.tenantId) &&
      dispatchCreateBasketsAction(
        dispatch,
        createBasketsAction,
        orderDetails?.tenantId,
        userOrderState,
        qrCodeScanTime,
      );
  }
};

const orderDetailsSuccessCallback = (count, setRetryCount, dispatchGetPostOrderTimer) => {
  if (setRetryCount) {
    dispatchGetPostOrderTimer = setTimeout(() => {
      setRetryCount(count);
      dispatchGetPostOrderTimer && clearTimeout(dispatchGetPostOrderTimer);
    }, MAX_RETRY_INTERVAL);
  }
};

export const dispatchGetPastOrderAction = (dispatch, orderDetails, retryCount = 0, setRetryCount = null) => {
  let dispatchGetPostOrderTimer = null;
  const request = {
    emailId: orderDetails?.userEmail,
    phoneNo: orderDetails?.userPhone,
    orderNo: orderDetails?.posOrderId,
    tenantId: orderDetails?.tenantId,
  };
  const count = retryCount + 1;
  dispatch(
    getPastOrderAction(request, () => orderDetailsSuccessCallback(count, setRetryCount, dispatchGetPostOrderTimer)),
  );
};

export const dispatchGetPostOrderAccepted = (dispatch, orderDetails, retryCount = 0, setRetryCount = null) => {
  let dispatchGetPostOrderTimer = null;
  const request = {
    orderId: orderDetails?.orderId,
    tenantId: orderDetails?.tenantId,
    customerId: orderDetails?.customerId,
  };
  const count = retryCount + 1;
  dispatch(
    getPostOrderAccepted(request, () => orderDetailsSuccessCallback(count, setRetryCount, dispatchGetPostOrderTimer)),
  );
};

export const dispatchResetPostOrder = dispatch => {
  return dispatch(resetPostOrder());
};

export const dispatchResetOrderAction = dispatch => {
  return dispatch(resetOrderAction());
};

export const dispatchResetPaymentStatusAction = dispatch => {
  return dispatch(resetPaymentStatus());
};

export const dispatchClearPastOrderAction = dispatch => {
  return dispatch(clearPastOrderAction({ value: [] }));
};

export const clearLocalStorageOrderTracking = () => {
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.ORDER_TRACKING);
};

export const dispatchGetPastOrderDetails = (dispatch, orderDetails, retryCount = 0, setRetryCount = null) => {
  if (orderDetails?.isExternalTrackOrder) {
    dispatchGetPastOrderAction(dispatch, orderDetails, retryCount, setRetryCount);
  } else {
    dispatchGetPostOrderAccepted(dispatch, orderDetails, retryCount, setRetryCount);
  }
};

export const postOrderApiCall = (dispatch, orderDetails, retryCount, postOrderDetails, setRetryCount, props) => {
  if (orderDetails?.orderId) {
    if (retryCount === 0 || (isStagedAcceptedOrder(postOrderDetails) && retryCount < MAX_RETRY_COUNT)) {
      dispatchGetPastOrderDetails(dispatch, orderDetails, retryCount, setRetryCount);
    } else if (isStagedAcceptedOrder(postOrderDetails) && retryCount >= MAX_RETRY_COUNT) {
      localStorage.setItem('paymentStatus', PAYMENT_STATUS.FAILED);
      redirectToPaymentFailurePage(props);
    }
  } else {
    redirectToHomePage(props);
  }
};

export const updateOrderDetails = (orderAccepted, orderDetails, setOrderDetails, checkDeliveryPOSCompleted, props) => {
  if (checkDeliveryPOSCompleted && isDeliveryDistaOrderPOSCompleted(orderAccepted)) {
    redirectToPaymentFailurePage(props);
  } else if (orderAccepted?.status && orderAccepted?.status !== orderDetails?.status) {
    setOrderDetails({
      ...orderDetails,
      status: orderAccepted?.status,
      orderAcceptedLoading: orderAccepted?.orderAcceptedLoading,
      deliveryPartner: orderAccepted?.deliveryPartner,
      deliveryDistaRetryCountFlag: orderDetails?.deliveryDistaRetryCountFlag + 1,
    });
  } else {
    setOrderDetails({
      ...orderDetails,
      deliveryDistaRetryCountFlag: orderDetails?.deliveryDistaRetryCountFlag + 1,
    });
  }
};

export const onDeliveryDistaValidateAndRedirection = (
  orderAccepted,
  checkDeliveryPOSCompleted,
  setCheckDeliveryPOSCompleted,
) => {
  if (isDeliveryDistaOrderPOSCompleted(orderAccepted) && !checkDeliveryPOSCompleted) {
    const maxWaitDelivery = setTimeout(() => {
      maxWaitDelivery && clearTimeout(maxWaitDelivery);
    }, MAX_WAIT_DELIVERY_INTERVAL);
  }
};

export const updateDefaultOrderDetails = (tenantId, postOrderReducer, orderReducerResponse, dispositionPaymentPage) => {
  return {
    ...updatePostOrderDetails(tenantId, postOrderReducer, false, orderReducerResponse),
    orderAcceptedLoading: postOrderReducer?.orderAcceptedLoading || false,
    deliveryPartner: null,
    deliveryDistaRetryCountFlag: 0,
    operatingTime: dispositionPaymentPage?.store?.summary?.timings,
  };
};

export const updatePostOrderDetails = (
  tenantId,
  postOrderDetails,
  isExternalTrackOrder = false,
  currentOrderDetails = {},
  storesReducer = {},
) => {
  const returnOrderDetails = {
    orderId: postOrderDetails?.id || currentOrderDetails?.id,
    customerId: postOrderDetails?.customer?.id || currentOrderDetails?.customer?.id,
    tenantId: tenantId,
    service: postOrderDetails?.service || currentOrderDetails?.service,
    status: postOrderDetails?.status || currentOrderDetails?.status,
    orderTime: postOrderDetails?.fulfillmentDateTime || currentOrderDetails?.fulfillmentDateTime,
    deliveryPartner: postOrderDetails?.deliveryPartner || currentOrderDetails?.deliveryPartner,
    posOrderId: postOrderDetails?.posOrderId || currentOrderDetails?.posOrderId,
    userEmail: postOrderDetails?.customer?.email || currentOrderDetails?.customer?.email,
    userPhone:
      postOrderDetails?.customer?.phoneNumber ||
      postOrderDetails?.customer?.phone ||
      currentOrderDetails?.customer?.phoneNumber,
    postOrderDetails: postOrderDetails,
    isExternalTrackOrder: isExternalTrackOrder,
  };

  if (storesReducer?.storesWithStatus?.length) {
    returnOrderDetails.operatingTime = storesReducer?.storesWithStatus?.[0]?.summary?.timings;
  }
  return returnOrderDetails;
};

export const getOrderReducerPastOrderResponse = orderReducer => {
  return(
    orderReducer?.pastOrderResponse?.length &&
    orderReducer?.pastOrderResponse[0].id
    ? orderReducer?.pastOrderResponse[0]
    : null
  )
};

export const getPostOrderReducerOrderAccepted = postOrderReducer => {
  return postOrderReducer?.orderAccepted &&
    Object.keys(postOrderReducer?.orderAccepted).length &&
    postOrderReducer?.orderAccepted?.id
    ? postOrderReducer?.orderAccepted
    : null;
};

export const getPostOrderDetails = (
  postOrderReducer = {},
  orderReducer = {},
  orderDetails = {},
  isCheckOrderDetails = true,
) => {
  let returnPostOrderDetails = getPostOrderReducerOrderAccepted(postOrderReducer);
  if (returnPostOrderDetails === null) {
    returnPostOrderDetails = getOrderReducerPastOrderResponse(orderReducer);
    if (isCheckOrderDetails && returnPostOrderDetails === null) {
      returnPostOrderDetails =
        orderDetails && Object.keys(orderDetails?.postOrderDetails).length ? orderDetails?.postOrderDetails : {};
    }
  }
  return returnPostOrderDetails || {};
};

export const resetOrderReducers = (dispatch, beforeUnload = false) => {
  if (localStorage.getItem(LOCAL_STORAGE_KEYS.PAYMENT_STATUS) !== PAYMENT_STATUS.FAILED) {
    // RENDEV-17159 preventing this call when we reach to checkout out and payment success page it should return from this.
    if (
      (beforeUnload && window?.location?.pathname === ROUTE_URL?.PAYMENT_SUCCESS) ||
      window?.location?.pathname === ROUTE_URL?.CHECKOUT
    ) {
      return false;
    }
    dispatchResetPostOrder(dispatch);
    dispatchResetOrderAction(dispatch);
  }
  // RENDEV-19929 moved this function call(dispatchResetPaymentStatusAction) line down because it was resetting the payment reducer in checkout page intermittenly.
  dispatchResetPaymentStatusAction(dispatch);
  dispatchClearPastOrderAction(dispatch);
  clearLocalStorageOrderTracking();
};
